import { useState, useEffect } from "react";

export const useDeliverySession = () => {
  const defaultSession = {
    da_id: null,
    file_ident_code: "",
    company_nm: "",
    company_img: "",
    da_name: "",
    service_type: "dp_delivery",
    phone_number: "",
    login_id: "",
    total_no_of_delivery: 0,
    created_at: null,
    updated_at: null,
    finished_at: null,
    status: "inactive",
    web_status: "active",
    deliveries: [],
    building: null,
    is_allresident_auth_completed: "False",
    delivery_qr_code_shortlink: "",
    dp_web_session_otp: "",
    pending_delivery_id: null,
  };

  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem("deliverySession");
    return saved ? JSON.parse(saved) : defaultSession;
  });

  // Persist session changes to localStorage
  useEffect(() => {
    if (session && session !== null) {
      localStorage.setItem("deliverySession", JSON.stringify(session));
      console.log("📦 Synced session to localStorage:", session);
    }
  }, [session]);

  const addDelivery = (newDelivery) => {
    console.log("Adding single delivery:", newDelivery);
    setSession((prev) => {
      const updatedDeliveries = [...prev.deliveries, newDelivery];
      return {
        ...prev,
        deliveries: updatedDeliveries,
        total_no_of_delivery: updatedDeliveries.length,
      };
    });
  };

  const addDeliveriesAsync = (newDeliveries) =>
    new Promise((resolve) => {
      setSession((prev) => {
        const existingIds = new Set(prev.deliveries.map((d) => d.resident_id));
        const filteredNewDeliveries = newDeliveries.filter(
          (d) => !existingIds.has(d.resident_id),
        );
        const updatedDeliveries = [
          ...prev.deliveries,
          ...filteredNewDeliveries,
        ];
        const updatedSession = {
          ...prev,
          deliveries: updatedDeliveries,
        };
        localStorage.setItem("deliverySession", JSON.stringify(updatedSession));
        setTimeout(() => resolve(updatedSession), 0);
        return updatedSession;
      });
    });

  const updateDeliveryField = (key, value) => {
    setSession((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Remove a delivery by resident_id
  const removeDelivery = (residentId) => {
    console.log("Attempting to remove delivery with residentId:", residentId); // Debug log
    setSession((prev) => {
      const updatedDeliveries = prev.deliveries.filter(
        (d) => d.resident_id !== residentId,
      );
      const updatedSession = {
        ...prev,
        deliveries: updatedDeliveries,
        total_no_of_delivery: updatedDeliveries.length,
      };
      localStorage.setItem("deliverySession", JSON.stringify(updatedSession));
      console.log("New session after removal:", updatedSession); // Debug new state
      setTimeout(() => {}, 0); // Placeholder for consistency, no resolve needed
      return updatedSession;
    });
  };

  const clearSession = () => {
    setSession(defaultSession);
    localStorage.setItem("deliverySession", JSON.stringify(defaultSession));
  };

  // Inside useDeliverySession hook, add this new function
  const resetSessionForNextUse = () => {
    setSession((prev) => {
      const resetSession = {
        ...prev,
        deliveries: [], // or {} if you use object instead of array
        total_no_of_delivery: 0,
        status: "inactive",
        web_status: "active",
        delivery_qr_code_shortlink: "",
        dp_web_session_otp: "",
        pending_delivery_id: null, // optional: also reset this if needed
        // Keep everything else: da_id, company info, etc.
      };

      localStorage.setItem("deliverySession", JSON.stringify(resetSession));
      console.log("🧹 Session reset for next delivery batch:", resetSession);
      return resetSession;
    });
  };

  const sendToBackend = async () => {
    try {
      const response = await fetch(
        "https://your-api.com/api/delivery-session/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(session),
        },
      );
      const data = await response.json();
      if (response.ok) {
        console.log("✅ Delivery session sent successfully", data);
        return { success: true, data };
      } else {
        console.error("❌ Backend returned error", data);
        return { success: false, data };
      }
    } catch (error) {
      console.error("🚨 Network error while sending delivery session", error);
      return { success: false, error };
    }
  };

  return {
    session,
    setSession,
    addDelivery,
    addDeliveriesAsync,
    removeDelivery,
    updateDeliveryField,
    sendToBackend,
    clearSession,
    resetSessionForNextUse, // new: partial reset after successful submit
  };
};
